import React from 'react';
import {
  UserIcon,
  CalendarIcon,
  TruckIcon,
  TicketIcon,
  DocumentIcon,
  ChatBubbleLeftIcon,
  CubeIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  InformationCircleIcon,
  ArrowTopRightOnSquareIcon,
} from '@heroicons/react/24/outline';

/**
 * Bespoke "ship" icon — Heroicons doesn't ship a maritime icon; this is the
 * exact SVG used in the prototype `Icon.ship` definition.
 */
const ShipIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M2 18s2 2 5 2 5-2 5-2 2 2 5 2 5-2 5-2" />
    <path d="M4 13l8-3 8 3M12 4v6" />
  </svg>
);

/**
 * Filled "location" pin (concentric teardrop), used for the Delivery Location
 * card. Fill-based (inherits currentColor) rather than stroked.
 */
const LocationIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="m19.071,2.929c-1.89-1.889-4.4-2.929-7.071-2.929s-5.182,1.04-7.071,2.929c-3.899,3.899-3.899,10.243.004,14.146l6.059,5.925h-5.991v1h14v-1h-5.991l6.063-5.929c3.899-3.899,3.899-10.243,0-14.143Zm-7.071,19.659l-6.364-6.224c-3.509-3.51-3.509-9.219,0-12.729,1.7-1.699,3.961-2.636,6.364-2.636s4.664.937,6.364,2.636c3.509,3.51,3.509,9.22.004,12.725l-6.368,6.228ZM7,6.894v6.665l5,2.5,5-2.5v-6.665l-5-2.45-5,2.45Zm8.382.32l-3.382,1.725-3.382-1.725,3.382-1.657,3.382,1.657Zm-3.382,7.728l-4-2v-4.92l4,2.04,4-2.04v4.92l-4,2Z" />
  </svg>
);

/**
 * Filled "map" (folded panels) icon, used for the Rate / Delivery Zone card.
 * Fill-based (inherits currentColor) rather than stroked.
 */
const MapIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="m22.143,1.657L17.593.134c-.515-.172-1.06-.181-1.552-.029l-7.187,1.826c-.317.097-.653.091-.944-.006L3.588.202C2.786-.133,1.876-.043,1.154.437.432.918,0,1.725,0,2.593v19.202l8.497,2.221,8.005-2.001,7.498,1.812V4.142c0-1.151-.772-2.175-1.857-2.485Zm-6.143-.51v19.963l-7,1.75V2.928c.045-.011,7-1.781,7-1.781ZM1,2.593c0-.542.259-1.024.709-1.324.45-.3.995-.351,1.501-.141l4.355,1.736c.143.048.288.084.435.106v19.882l-7-1.829V2.593Zm22,19.963l-6-1.45V1.017c.092.014,4.846,1.595,4.846,1.595.68.194,1.154.823,1.154,1.53v18.415Z" />
  </svg>
);

/**
 * Filled "ruler/right-triangle" icon, used for the chassis Size Specifications
 * trigger (V1's literal triangle icon asset wasn't recoverable from the source
 * repos, so this hand-supplied SVG replaces the earlier ExclamationTriangleIcon
 * placeholder). Fill-based (inherits currentColor) rather than stroked.
 */
const RulerTriangleIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M22.824,17.179L6.821,1.177C5.656,.012,3.986-.322,2.467,.311,.945,.94,0,2.354,0,4.002v14.991c0,2.761,2.246,5.007,5.007,5.007h14.991c1.646,0,3.061-.945,3.691-2.467s.298-3.189-.866-4.354Zm-.981,3.59c-.32,.771-1.009,1.231-1.844,1.231H5.007c-1.658,0-3.007-1.349-3.007-3.007V4.002c0-.835,.46-1.524,1.232-1.844,.261-.108,.526-.161,.787-.161,.509,0,.998,.203,1.388,.594l1.611,1.611-1.908,1.908c-.391,.391-.391,1.023,0,1.414,.195,.195,.451,.293,.707,.293s.512-.098,.707-.293l1.908-1.908,1.466,1.466-1.908,1.908c-.391,.391-.391,1.023,0,1.414,.195,.195,.451,.293,.707,.293s.512-.098,.707-.293l1.908-1.908,1.415,1.414-1.908,1.908c-.391,.391-.391,1.023,0,1.414,.195,.195,.451,.293,.707,.293s.512-.098,.707-.293l1.908-1.908,1.414,1.414-1.908,1.908c-.391,.391-.391,1.023,0,1.414,.195,.195,.451,.293,.707,.293s.512-.098,.707-.293l1.908-1.908,1.414,1.414-1.908,1.908c-.391,.391-.391,1.023,0,1.414,.195,.195,.451,.293,.707,.293s.512-.098,.707-.293l1.908-1.908,1.611,1.611c.59,.591,.752,1.404,.433,2.176Zm-11.135-4.476c.286,.286,.372,.716,.217,1.09s-.52,.617-.924,.617h-3c-.552,0-1-.447-1-1v-3c0-.404,.244-.77,.617-.924,.374-.156,.804-.069,1.09,.217l3,3Z" />
  </svg>
);

/**
 * Named icon registry mirroring the prototype's `Icon` object. Each entry is
 * a React component accepting standard SVG props (className, style, …).
 *
 * Where a Heroicon outline shape matches the prototype's hand-rolled SVG, we
 * use the Heroicon for consistency with the rest of the app. The bespoke
 * `ship` icon is preserved verbatim because Heroicons has no equivalent.
 */
export const Icon = {
  user: UserIcon,
  calendar: CalendarIcon,
  truck: TruckIcon,
  ticket: TicketIcon,
  doc: DocumentIcon,
  comment: ChatBubbleLeftIcon,
  pkg: CubeIcon,
  ship: ShipIcon,
  location: LocationIcon,
  map: MapIcon,
  warn: ExclamationTriangleIcon,
  chevron: ChevronDownIcon,
  search: MagnifyingGlassIcon,
  info: InformationCircleIcon,
  external: ArrowTopRightOnSquareIcon,
  rulerTriangle: RulerTriangleIcon,
} as const;

export type IconName = keyof typeof Icon;

export default Icon;
